import React, { Component, useRef, useState, useEffect } from 'react';
import { useParams } from "react-router-dom";

import LoadingAnimation from '../LoadingAnimation.js';
import * as MastersUtils from './MastersUtils.js';
import NavBarMini from '../NavBarMini.js';
import Footer from '../Footer.js';

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc } from "firebase/firestore";
import { db } from '../../firebase.js';

import mastersData2023 from './mastersData2023.json';
import mastersData2024 from './mastersData2024.json';
import mastersData2025 from './mastersData2025.json';


const currentYear = '2026'
let mastersYear = '2026'
let mastersYearData = 'data2026'

const defaultMastersData = {
  currentRound: '1000',
  statusRound: 'NNNNN',
  wallClockTime: '0',
  player: [],
  yardages: [],
  pars: []
}

const formatSignedScore = (score) => {
  if (typeof score !== 'number') return score;
  if (score > 0) return `+${score}`;
  return `${score}`;
};

const PlayerNetScoreCell = ({ player }) => {
  const [showPopover, setShowPopover] = useState(false);
  const cellRef = useRef(null);

  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = (e) => {
      if (cellRef.current && !cellRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPopover]);

  if (player.status === 'W') {
    return <td className="masters-table-data total extra-width withdrew"><span>WD</span></td>;
  }

  const netScore = player.scoreNet;
  const bd = player.scoreBreakdown;

  let classList = ['masters-table-data', 'total', 'extra-width', 'net-breakdown-cell'];
  if (player.status === 'C') {
    classList.push('missed-cut');
  } else if (typeof netScore === 'number') {
    classList.push(netScore >= 0 ? 'not-below-par' : 'below-par');
  }

  const renderBreakdown = () => {
    if (!bd) return null;
    const { gross, isScottie, wildcardApplies, positionBonus, missedCut, mcBaseScore } = bd;

    if (missedCut) {
      const net = mcBaseScore + 16;
      return (
        <>
          <div className="net-breakdown-line">
            <span>Gross</span><span>{formatSignedScore(mcBaseScore)}</span>
          </div>
          <div className="net-breakdown-line">
            <span>Missed Cut (+8 x2)</span><span>{formatSignedScore(net)}</span>
          </div>
          <div className="net-breakdown-line net-breakdown-net">
            <span>Net</span><span>{formatSignedScore(net)}</span>
          </div>
        </>
      );
    }

    let running = gross;
    const lines = [];

    lines.push(
      <div key="gross" className="net-breakdown-line">
        <span>Gross</span><span>{formatSignedScore(gross)}</span>
      </div>
    );

    if (isScottie) {
      running += 2;
      lines.push(
        <div key="scottie" className="net-breakdown-line">
          <span>Scottie +2</span><span>{formatSignedScore(running)}</span>
        </div>
      );
    }

    if (wildcardApplies) {
      running += isScottie ? (2 + gross) * 0.5 : gross * 0.5;
      lines.push(
        <div key="wildcard" className="net-breakdown-line">
          <span>Wildcard x1.5</span><span>{formatSignedScore(running)}</span>
        </div>
      );
    }

    if (positionBonus !== 0) {
      running += positionBonus;
      const bonusLabel = positionBonus === -3 ? 'Leader (-3)' : positionBonus === -2 ? 'Top 5 (-2)' : 'Top 10 (-1)';
      lines.push(
        <div key="position" className="net-breakdown-line">
          <span>{bonusLabel}</span><span>{formatSignedScore(running)}</span>
        </div>
      );
    }

    lines.push(
      <div key="net" className="net-breakdown-line net-breakdown-net">
        <span>Net</span><span>{formatSignedScore(netScore)}</span>
      </div>
    );

    return lines;
  };

  return (
    <td ref={cellRef} className={classList.join(' ')} onClick={() => setShowPopover(!showPopover)}>
      <span>{player.status === 'C' ? 'MC' : (typeof netScore === 'number' ? Math.abs(netScore) : netScore)}</span>
      {showPopover && (
        <div className="net-breakdown-popover">
          {renderBreakdown()}
        </div>
      )}
    </td>
  );
};

const PoolLeaderboard = () => {
  const [leaderboardReady, setLeaderboardReady] = useState(false);
  const [masters, setMasters] = useState(defaultMastersData);
  const [users, setUsers] = useState([]);
  const { year } = useParams();

  useEffect(() => {
    mastersYear = year || currentYear
    mastersYearData = `data${mastersYear}`

    document.title = `${mastersYear} 1A Masters Pool`;

    getUsers();
    getMastersData();

    setLeaderboardReady(true);
  }, [])

  // Fetch users from Firebase DB
  const getUsers = async () => {
    const docs = await getDocs(collection(db, "masters", mastersYearData, "users"));

    let users = [];
    docs.forEach((doc) => {
      const userID = doc.id;
      const user = doc.data();
      user.id = userID;
      users.push(user);
    });

    setUsers(users);
  }

  // Fetch Masters data locally or from API endpoint
  const getMastersData = async () => {
    const localData = { '2023': mastersData2023, '2024': mastersData2024, '2025': mastersData2025 };
    if (localData[mastersYear]) {
      setMasters(localData[mastersYear].data);
      return;
    }
    fetch(`https://www.masters.com/en_US/scores/feeds/${mastersYear}/scores.json`)
      .then((response) => response.json())
      .then((data) => {
        if (data.data && data.data.player) {
          setMasters(data.data);
        }
      })
  }

  function displayUserAndPlayers (user, winningScore) {
    return (
      <>
        { displayUser(user, winningScore) }
        { displayPlayersForUser(user) }
      </>
    )
  }
  function displayUser (user, winningScore) {
    let rowClassList = ['clickable'];
    if (winningScore !== null && user.scoreNet === winningScore) {
      rowClassList.push('winner');
    }

    return (
      <tr id={user.lastName + "-user-row"} className={ rowClassList.join(' ') } onClick={() => MastersUtils.showPlayers(user.lastName + "-user-row")}>
        <td className="masters-table-data-name user-name">{user.lastName.toUpperCase() }</td>
        { userHoles(user) }
        { MastersUtils.userGrossScore(user) }
        { MastersUtils.userNetScore(user) }
      </tr>
    );
  }
  function userHoles (user) {
    let roundNumber = MastersUtils.getCurrentRoundNumber(masters)
    let [playerScores, maxThru] = MastersUtils.getPlayerHoleScores(user.playersSorted.slice(0,5), roundNumber, masters.pars.round1)
    let userScore = playerScores.reduce((a, b) => a.map((v, i) => v + b[i]));

    userScore = userScore.slice(0, maxThru).concat(Array(18 - maxThru).fill(''))

    let scores = userScore.map((score) => MastersUtils.userHoleScore(score))
    return scores;
  }

  function displayPlayersForUser (user) {
    let roundNumber = MastersUtils.getCurrentRoundNumber(masters)
    let playerElements = user.playersSorted.map((player) => {
      return (
        <tr id={user.lastName + "-" + player.id + "-row"} className={user.lastName + "-user-row user-player-row hidden"}>
          <td className="masters-table-cell player-name">
            <div className='masters-player-cell-name-line'><p className="masters-player-cell-flag">{ MastersUtils.emojiFlagForCountryCode(player.countryCode) }</p><p className={`masters-player-cell-name ${ player.id == user.golfers.wildcard ? 'wildcard' : '' }`}>{ player.display_name.toUpperCase() }</p></div>
            <div className='masters-player-cell-info'>
              <div className="masters-player-cell-place-label">Place: { player.pos == '' ? '-' : `${player.pos}` }</div>
              <div className="masters-player-cell-tier-label">{ MastersUtils.playerTierLabel(user, player, false) }</div>
            </div>
          </td>
          { MastersUtils.playerRoundScores(player, roundNumber, masters.pars.round1) }
          { MastersUtils.playerGrossScore(player) }
          <PlayerNetScoreCell player={player} />
        </tr>
      )
      return (
        <tr id={user.lastName + "-" + player.id + "-row"} className={user.lastName + "-user-row user-player-row hidden"}>
          <td className={`masters-table-data-name player-name ${ player.id == user.golfers.wildcard ? 'wildcard' : '' }` }>
            <img className="masters-player-flag" src={`https://www.masters.com/assets/images/flags/${player.countryCode}_sm.gif`}/>
            { player.pos } { player.display_name.toUpperCase() } <div className="player-tier-label">{ MastersUtils.playerTierLabel(user, player) }</div></td>
          { MastersUtils.playerRoundScores(player, roundNumber, masters.pars.round1) }
          { MastersUtils.playerGrossScore(player) }
          { MastersUtils.playerNetScore(player, roundNumber) }
        </tr>
      )
    });
    return playerElements;
  }

  function displayUserTableRows () {
    if (mastersYear !== currentYear || MastersUtils.tournamentStarted()) {
      let sortedUsers = MastersUtils.sortUsersAndPlayers(users, masters);

      const winningScore = MastersUtils.tournamentEnded(masters) && sortedUsers.length > 0
        ? sortedUsers[0].scoreNet
        : null;

      let userAndPlayerElements = sortedUsers.map((user) => displayUserAndPlayers(user, winningScore));

      return userAndPlayerElements;
    } else {
      return users.map((user) => (
        <tr key={user.id}>
          <td className="masters-table-data-name user-name">{ user.lastName.toUpperCase() }</td>
          { Array.from({ length: 18 }, (_, i) => <td key={i} className="masters-table-data hole"><span>&#160;</span></td>) }
          <td className="masters-table-data total extra-width"><span>&#160;</span></td>
          <td className="masters-table-data total extra-width"><span>&#160;</span></td>
        </tr>
      ));
    }
  }

  function displayHelperText () {
    if (mastersYear !== currentYear || MastersUtils.tournamentStarted()) {
      if (MastersUtils.tournamentEnded(masters)) {
        return (
          <></>
        );
      } else {
        return (
          <div>
            <p className="masters-type-paragraph large-screen-only">Leaderboard extends through the hole of your furthest along top-5 player and shows your gross score (though bonuses are not shown, users are ranked by net score with bonuses).</p>
            <p className="masters-type-paragraph small-screen-only">Gross score shows sum of your top 5 players.</p>
            <p className="masters-type-paragraph-small small-screen-only">Net score factors in bonus points.</p>
            <p className="masters-type-paragraph-small small-screen-only">1st Place: -3</p>
            <p className="masters-type-paragraph-small small-screen-only">Top-5: -2</p>
            <p className="masters-type-paragraph-small small-screen-only">Top-10: -1</p>
            <p className="masters-type-paragraph-small small-screen-only">WC: x1.5 (under par only)</p>
            <p className="masters-type-paragraph-small small-screen-only">Scottie Scheffler: +2</p>
          </div>
        );
      }
    } else {
      return (
        <>
          <p className="masters-type-paragraph">Leaderboard will update once the tournament has begun.</p>
          <a href="/masters/new" className="btn btn-primary masters-btn">Join Masters Pool</a>
        </>
      );
    }
  }

  if (leaderboardReady == false) {
    return (
      <div className="Masters">
        <NavBarMini alwaysShown={true} />
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Masters">
        <NavBarMini alwaysShown={true} />
        <div className="masters-content">
          <div className="masters-header">
            <div className="masters-header-top">
              <p className={ "masters-title-standard" }>{ mastersYear } 1A Masters Pool</p>
              <a className="masters-type" href={ mastersYear == '2026' ? "/masters/augusta" : `/masters/augusta/${mastersYear}` }>Leaders At Augusta</a>
              <div className="masters-past-year-leaderboards">
                { mastersYear == '2023' ? <span className="masters-type masters-year-selected">2023</span> : <a className="masters-type" href="/masters/2023">2023</a> }
                { mastersYear == '2024' ? <span className="masters-type masters-year-selected">2024</span> : <a className="masters-type" href="/masters/2024">2024</a> }
                { mastersYear == '2025' ? <span className="masters-type masters-year-selected">2025</span> : <a className="masters-type" href="/masters/2025">2025</a> }
                { mastersYear == '2026' ? <span className="masters-type masters-year-selected">2026</span> : <a className="masters-type" href="/masters">2026</a> }
              </div>
              { displayHelperText() }
            </div>
          </div>


          <div className="masters-body">
            <div className="masters-table-container">
              <div className="cut-off-half"><div className="masters-table-heading-title"><span>LEADERS</span></div></div>

              <table className="table masters-table">
                <thead className="masters-table-heading-hole">
                  <tr>
                    <th className="masters-table-heading-name"><span>HOLE</span></th>
                    <th className="masters-table-heading hole"><span>1</span></th>
                    <th className="masters-table-heading hole"><span>2</span></th>
                    <th className="masters-table-heading hole"><span>3</span></th>
                    <th className="masters-table-heading hole"><span>4</span></th>
                    <th className="masters-table-heading hole"><span>5</span></th>
                    <th className="masters-table-heading hole"><span>6</span></th>
                    <th className="masters-table-heading hole"><span>7</span></th>
                    <th className="masters-table-heading hole"><span>8</span></th>
                    <th className="masters-table-heading hole"><span>9</span></th>
                    <th className="masters-table-heading hole"><span>10</span></th>
                    <th className="masters-table-heading hole"><span>11</span></th>
                    <th className="masters-table-heading hole"><span>12</span></th>
                    <th className="masters-table-heading hole"><span>13</span></th>
                    <th className="masters-table-heading hole"><span>14</span></th>
                    <th className="masters-table-heading hole"><span>15</span></th>
                    <th className="masters-table-heading hole"><span>16</span></th>
                    <th className="masters-table-heading hole"><span>17</span></th>
                    <th className="masters-table-heading hole"><span>18</span></th>
                  </tr>
                </thead>
                <thead className="masters-table-heading-par">
                <tr>
                  <th className="masters-table-heading-name"><span>PAR</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>5</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>3</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>3</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>5</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>3</span></th>
                  <th className="masters-table-heading hole"><span>5</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>5</span></th>
                  <th className="masters-table-heading hole"><span>3</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                  <th className="masters-table-heading hole"><span>4</span></th>
                </tr>
                </thead>
                <thead className="masters-table-heading-total">
                  <tr>
                    <th className="masters-table-heading-name"><span>NAME</span></th>
                    <th className="masters-table-heading-small"><span>GROSS</span></th>
                    <th className="masters-table-heading-small"><span>NET</span></th>
                  </tr>
                </thead>
                <tbody>
                  { displayUserTableRows() }
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default PoolLeaderboard;
